/**
 * Run correction (console form of correct_run). uc-core RunCorrectionDto + RunCorrectionService:
 *   always       exceptionId (an OPEN problem of the run), kind, reasonCode (cause)
 *   REWEIGH_OUTPUT  the sack (containerId or tag) + weightReadG (scale reading, whole grams); sackTypeId?
 *   ADD_OUTPUT      streamKey (an output of the step, or FLUSH) + tag + sackTypeId + weightReadG
 *   ADD_INPUT       the bag (tag or containerId); weightReadG only when it was never weighed
 *   VOID_OUTPUT     the sack (containerId or tag)
 * The reading must be more than the sack's own weight (the server refuses otherwise).
 */
import { decimalToMinor } from '../../api/mappers'
import type { CorrectionBody, CorrectionKind, RunCause } from '../../api/process'

export interface CorrectionDraft {
  exceptionId: string
  kind: CorrectionKind | ''
  reasonCode: RunCause | ''
  /** Output sack picked from the run (REWEIGH / VOID). */
  containerId: string
  /** Tag typed or scanned (ADD_OUTPUT, ADD_INPUT; or instead of a picked sack). */
  qrPayload: string
  /** Scale reading in kg, as typed. */
  weightKg: string
  sackTypeId: string
  streamKey: string
}

export type CorrectionProblems = Partial<Record<keyof CorrectionDraft, string>>

export const emptyCorrectionDraft = (p: Partial<CorrectionDraft> = {}): CorrectionDraft => ({
  exceptionId: '',
  kind: '',
  reasonCode: '',
  containerId: '',
  qrPayload: '',
  weightKg: '',
  sackTypeId: '',
  streamKey: '',
  ...p,
})

const KG_RE = /^\d{1,6}(\.\d{1,3})?$/

/** "45.5" → 45500 grams; null when not a reading. */
export function readingGrams(kg: string): number | null {
  const v = kg.trim().replace(/,/g, '')
  if (!KG_RE.test(v)) return null
  return decimalToMinor(v, 3)
}

/** Which fields a kind asks for. */
export function correctionFields(kind: CorrectionKind | '') {
  return {
    pickSack: kind === 'REWEIGH_OUTPUT' || kind === 'VOID_OUTPUT',
    tag: kind === 'ADD_OUTPUT' || kind === 'ADD_INPUT',
    weight: kind === 'REWEIGH_OUTPUT' || kind === 'ADD_OUTPUT' || kind === 'ADD_INPUT',
    weightRequired: kind === 'REWEIGH_OUTPUT' || kind === 'ADD_OUTPUT',
    sackType: kind === 'REWEIGH_OUTPUT' || kind === 'ADD_OUTPUT',
    sackTypeRequired: kind === 'ADD_OUTPUT',
    streamKey: kind === 'ADD_OUTPUT',
  }
}

/**
 * @param tareG the chosen sack type's own weight (grams) when known — the reading must be above it.
 */
export function validateCorrection(d: CorrectionDraft, opts: { tareG?: number | null; streamKeys?: readonly string[] } = {}): CorrectionProblems {
  const p: CorrectionProblems = {}
  if (!d.exceptionId) p.exceptionId = 'Choose the problem this correction is for'
  if (!d.kind) p.kind = 'Choose what to correct'
  if (!d.reasonCode) p.reasonCode = 'Choose the cause'
  const f = correctionFields(d.kind)
  const tag = d.qrPayload.trim()
  if (tag.length > 64) p.qrPayload = 'At most 64 characters'
  if (f.pickSack && !d.containerId && !tag) p.containerId = 'Choose the sack'
  if (f.tag && !tag && !(d.kind === 'ADD_INPUT' && d.containerId)) p.qrPayload = d.kind === 'ADD_INPUT' ? 'Scan or type the bag tag' : 'Scan or type the new sack tag'
  if (f.streamKey) {
    if (!d.streamKey) p.streamKey = 'Choose which output'
    else if (opts.streamKeys && !opts.streamKeys.includes(d.streamKey)) p.streamKey = 'Not an output of this step'
  }
  if (f.sackTypeRequired && !d.sackTypeId) p.sackTypeId = 'Choose the sack type'
  if (f.weight) {
    const w = d.weightKg.trim()
    if (!w) {
      if (f.weightRequired) p.weightKg = 'Enter what the scale shows'
    } else {
      const g = readingGrams(w)
      if (g === null) p.weightKg = 'Kilos with up to 3 decimals, e.g. 45.5'
      else if (g <= 0) p.weightKg = 'Must be more than 0'
      else if (opts.tareG != null && f.sackType && g <= opts.tareG) p.weightKg = 'Must be more than the empty sack weighs'
    }
  }
  return p
}

export const correctionOk = (p: CorrectionProblems) => Object.values(p).every((v) => !v)

/** POST /process-runs/:id/corrections body. Call only when valid. */
export function correctionBody(d: CorrectionDraft): CorrectionBody {
  const f = correctionFields(d.kind)
  const tag = d.qrPayload.trim()
  const g = f.weight && d.weightKg.trim() ? readingGrams(d.weightKg) : null
  return {
    exceptionId: d.exceptionId,
    kind: d.kind as CorrectionKind,
    reasonCode: d.reasonCode as RunCause,
    ...(f.pickSack && d.containerId ? { containerId: d.containerId } : {}),
    ...(d.kind === 'ADD_INPUT' && d.containerId && !tag ? { containerId: d.containerId } : {}),
    ...((f.tag || (f.pickSack && !d.containerId)) && tag ? { qrPayload: tag } : {}),
    ...(g !== null ? { weightReadG: g } : {}),
    ...(f.sackType && d.sackTypeId ? { sackTypeId: d.sackTypeId } : {}),
    ...(f.streamKey && d.streamKey ? { streamKey: d.streamKey } : {}),
  }
}
